/**
 * @fileoverview Owns shared client behavior and exposes its stable module boundary.
 */

export const PIECE_ATLAS_VERSION = "20260711-bingo-balls";

export const PIECE_ATLAS_SRC = `/assets/atlases/pieces.png?v=${PIECE_ATLAS_VERSION}`;

export const PIECE_ATLAS_SIZE = 156;

export const PIECE_ATLAS_COLUMNS = 9;

export const PIECE_ATLAS_ROWS = 9;

export const PIECE_ATLAS_WIDTH = PIECE_ATLAS_SIZE * PIECE_ATLAS_COLUMNS;

export const PIECE_ATLAS_HEIGHT = PIECE_ATLAS_SIZE * PIECE_ATLAS_ROWS;

export const PIECE_ATLAS_ITEMS = [
  atlasItem("token-red", 0, 0),
  atlasItem("token-green", 1, 0),
  atlasItem("token-yellow", 2, 0),
  atlasItem("token-blue", 3, 0),
  atlasItem("checker-white", 4, 0),
  atlasItem("checker-black", 5, 0),
  atlasItem("token-black", 6, 0),
  atlasItem("token-white", 7, 0),
  atlasItem("battleship-ship-2", 0, 1, 2),
  atlasItem("battleship-ship-3", 0, 2, 3),
  atlasItem("battleship-ship-4", 0, 3, 4),
  atlasItem("battleship-ship-5", 0, 4, 5),
  atlasItem("chess-black-rook", 0, 5),
  atlasItem("chess-black-knight", 1, 5),
  atlasItem("chess-black-bishop", 2, 5),
  atlasItem("chess-black-queen", 3, 5),
  atlasItem("chess-black-king", 4, 5),
  atlasItem("chess-black-pawn", 5, 5),
  atlasItem("chess-white-rook", 0, 6),
  atlasItem("chess-white-knight", 1, 6),
  atlasItem("chess-white-bishop", 2, 6),
  atlasItem("chess-white-queen", 3, 6),
  atlasItem("chess-white-king", 4, 6),
  atlasItem("chess-white-pawn", 5, 6),
  atlasItem("bingo-ball-blue", 0, 7),
  atlasItem("bingo-ball-red", 1, 7),
  atlasItem("bingo-ball-green", 2, 7),
  atlasItem("bingo-ball-yellow", 3, 7),
  atlasItem("bingo-ball-purple", 4, 7),
  atlasItem("bingo-ball-orange", 5, 7),
  atlasItem("bingo-ball-cyan", 6, 7),
  atlasItem("bingo-ball-pink", 7, 7)
];

export const PIECE_ATLAS_IDS = PIECE_ATLAS_ITEMS.map((item) => item.id);

export const PIECE_ATLAS_BACKGROUND_SIZE = `${PIECE_ATLAS_COLUMNS * 100}% ${PIECE_ATLAS_ROWS * 100}%`;

/**
 * Returns the piece atlas info used by shared client behavior.
 * @param {string} pieceOrId - Stable identifier of the target record or runtime entity.
 * @returns {unknown} The resulting piece atlas info.
 */
export function pieceAtlasInfo(pieceOrId) {
  const id = normalizePieceId(pieceOrId);
  const item = PIECE_ATLAS_ITEMS.find((candidate) => candidate.id === id);
  if (!item) return null;
  return {
    ...item,
    positionX: percentForSprite(item.x, item.width, PIECE_ATLAS_WIDTH),
    positionY: percentForSprite(item.y, item.height, PIECE_ATLAS_HEIGHT),
    backgroundSizeX: (PIECE_ATLAS_WIDTH / item.width) * 100,
    backgroundSizeY: (PIECE_ATLAS_HEIGHT / item.height) * 100
  };
}

/**
 * Returns the piece atlas style used by shared client behavior.
 * @param {string} pieceOrId - Stable identifier of the target record or runtime entity.
 * @returns {unknown} The resulting piece atlas style.
 */
export function pieceAtlasStyle(pieceOrId) {
  const info = pieceAtlasInfo(pieceOrId);
  if (!info) return "";
  return [
    `background-image:url(${PIECE_ATLAS_SRC})`,
    `background-position:${formatPercent(info.positionX)} ${formatPercent(info.positionY)}`,
    `background-size:${formatPercent(info.backgroundSizeX)} ${formatPercent(info.backgroundSizeY)}`,
    "background-repeat:no-repeat"
  ].join("; ");
}

function atlasItem(id, column, row, columnSpan = 1, rowSpan = 1) {
  return {
    id,
    column,
    row,
    columnSpan,
    rowSpan,
    x: column * PIECE_ATLAS_SIZE,
    y: row * PIECE_ATLAS_SIZE,
    width: columnSpan * PIECE_ATLAS_SIZE,
    height: rowSpan * PIECE_ATLAS_SIZE
  };
}

function normalizePieceId(pieceOrId) {
  if (!pieceOrId) return "";
  if (typeof pieceOrId === "object") return normalizePieceId(pieceOrId.id || pieceOrId.asset || "");
  const text = String(pieceOrId);
  if (PIECE_ATLAS_IDS.includes(text)) return text;
  const filename = text.split("/").pop()?.replace(/\.png$/i, "") || "";
  return PIECE_ATLAS_IDS.includes(filename) ? filename : "";
}

function percentForSprite(offset, spriteSize, atlasSize) {
  const range = atlasSize - spriteSize;
  return range <= 0 ? 0 : (offset / range) * 100;
}

function formatPercent(value) {
  return `${Math.round(value * 10000) / 10000}%`;
}
